import isUndefined from 'lodash/isUndefined.js';
import get from 'lodash/get.js';
import PendoInput from '../input/pendo-input.vue.js';
import PendoIcon from '../icon/pendo-icon.vue.js';
import { parseIncomingValue, decrease, increase, getPrecision, parseIncomingUnit, toPrecision, formatOutputValue } from './utils.js';
import { clamp, keyCodes } from '../../utils/utils.js';
import labelsMixin from '../../mixins/labels.js';

function _empty() {}
function _awaitIgnored(value, direct) {
  if (!direct) {
    return value && value.then ? value.then(_empty) : Promise.resolve();
  }
}
function _invoke(body, then) {
  var result = body();
  if (result && result.then) {
    return result.then(then);
  }
  return then(result);
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  name: 'PendoInputNumber',
  components: {
    PendoInput,
    PendoIcon
  },
  mixins: [labelsMixin],
  inject: {
    $form: {
      default: ''
    }
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    name: {
      type: String,
      default: ''
    },
    step: {
      type: Number,
      default: 1
    },
    controls: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    min: {
      type: Number,
      default: -Infinity
    },
    max: {
      type: Number,
      default: Infinity
    },
    value: {
      type: [Number, String],
      default: undefined
    },
    size: {
      type: String,
      default: 'medium',
      validator: size => ['mini', 'small', 'medium'].includes(size)
    },
    precision: {
      type: Number,
      default: 0,
      validator(val) {
        return val >= 0 && val === parseInt(val, 10);
      }
    },
    width: {
      type: String,
      default: null
    },
    alwaysShowArrows: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isFocused: false,
      userInput: null,
      currentValue: 0,
      unit: null
    };
  },
  computed: {
    parsedValue() {
      return parseIncomingValue({
        value: this.value,
        precision: this.precision
      });
    },
    isDisabled() {
      const isFormDisabled = get(this, '$form.disabled', false);
      return this.disabled || isFormDisabled;
    },
    minDisabled() {
      return decrease(this.parsedValue, this.step, this.numPrecision, this.currentValue) < this.min;
    },
    maxDisabled() {
      return increase(this.parsedValue, this.step, this.numPrecision, this.currentValue) > this.max;
    },
    numPrecision() {
      const {
        parsedValue,
        step,
        precision
      } = this;
      if (!isUndefined(precision)) {
        return precision;
      }
      const stepPrecision = getPrecision(step);
      const valuePrecision = getPrecision(parsedValue);
      return Math.max(valuePrecision, stepPrecision);
    },
    displayValue() {
      if (this.userInput !== null) {
        return this.userInput;
      }
      const {
        currentValue
      } = this;
      if (typeof currentValue === 'number' && !isUndefined(this.precision)) {
        return currentValue.toFixed(this.precision);
      }
      return currentValue;
    },
    ariaLabel() {
      if (this.$attrs && this.$attrs['aria-label']) {
        return this.$attrs['aria-label'];
      }
      return undefined;
    },
    ariaValueMax() {
      if (this.max === Infinity) {
        return Number.MAX_SAFE_INTEGER;
      }
      return this.max;
    },
    ariaValueMin() {
      if (this.min === -Infinity) {
        return Number.MIN_SAFE_INTEGER;
      }
      return this.min;
    }
  },
  watch: {
    parsedValue: 'onParsedValueChange'
  },
  created() {
    this.unit = parseIncomingUnit({
      value: this.value
    });
    this.onParsedValueChange(this.parsedValue);
  },
  methods: {
    onParsedValueChange(value) {
      let newVal = isUndefined(value) ? value : Number(value);
      if (!isUndefined(newVal)) {
        if (isNaN(newVal)) {
          return;
        }
        if (!isUndefined(this.precision)) {
          newVal = toPrecision(newVal, this.precision);
        }
      }
      if (newVal > this.max || newVal < this.min) {
        newVal = clamp(newVal, this.min, this.max);
        this.handleChange(newVal);
      }
      this.currentValue = newVal;
      this.userInput = null;
    },
    handleFocus(event) {
      this.isFocused = true;
      this.$emit('focus', event);
    },
    handleBlur(event) {
      this.isFocused = false;
      this.$emit('blur', event);
    },
    handleChange(value) {
      let newVal = value;
      if (typeof newVal === 'number' && !isUndefined(this.precision)) {
        newVal = toPrecision(newVal, this.precision);
      }
      if (newVal > this.max || newVal < this.min) {
        newVal = clamp(newVal, this.min, this.max);
      }
      if (newVal === this.currentValue) {
        return;
      }
      this.userInput = null;
      if (this.unit) {
        const {
          unit,
          precision
        } = this;
        this.$emit('change', formatOutputValue({
          value: newVal,
          precision,
          unit
        }));
      } else {
        this.$emit('change', newVal);
      }
      this.currentValue = newVal;
    },
    onControlsMouseup(handler) {
      if (new Date() - this.startTime < 100) {
        this[handler]();
      }
      this.interval = clearInterval(this.interval);
    },
    onControlsMousedown(handler) {
      this.startTime = new Date();
      clearInterval(this.interval);
      this.interval = setInterval(this[handler], 100);
    },
    increase: _async(function () {
      const _this = this;
      if (_this.isDisabled || _this.maxDisabled) {
        _this.interval = clearInterval(_this.interval);
        return;
      }
      return _invoke(function () {
        if (_this.userInput) {
          _this.handleTypedInputChange(_this.userInput);
          return _awaitIgnored(_this.$nextTick());
        }
      }, function () {
        const value = _this.parsedValue || 0;
        const newVal = increase(value, _this.step, _this.numPrecision, _this.currentValue);
        _this.handleChange(newVal);
      });
    }),
    decrease: _async(function () {
      const _this2 = this;
      if (_this2.isDisabled || _this2.minDisabled) {
        _this2.interval = clearInterval(_this2.interval);
        return;
      }
      return _invoke(function () {
        if (_this2.userInput) {
          _this2.handleTypedInputChange(_this2.userInput);
          return _awaitIgnored(_this2.$nextTick());
        }
      }, function () {
        const value = _this2.parsedValue || 0;
        const newVal = decrease(value, _this2.step, _this2.numPrecision, _this2.currentValue);
        _this2.handleChange(newVal);
      });
    }),
    handleTypedInput(value) {
      this.userInput = value;
    },
    handleTypedInputChange(value) {
      const newVal = value === '' ? undefined : Number(value);
      if (!isNaN(newVal) || value === '') {
        this.handleChange(newVal);
      }
      this.userInput = null;
    },
    handleKeydown(event) {
      if (event.keyCode === keyCodes.up) {
        event.preventDefault();
        this.increase();
      }
      if (event.keyCode === keyCodes.down) {
        event.preventDefault();
        this.decrease();
      }
      this.$emit('keydown', event);
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-input-number.vue2.js.map
